import { useState, useEffect, useCallback } from 'react'
import { Plus, Trash2, X, Target, Check } from 'lucide-react'
import { api } from '../services/api'
import { formatCurrency, getPercentage } from '../utils/format'

const priorityColors = {
  high: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', label: 'Haute' },
  medium: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', label: 'Moyenne' },
  low: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', label: 'Basse' },
}

export default function Projects() {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({ title: '', description: '', target_amount: '', saved_amount: '', target_month: '', priority: 'medium' })

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api.getProjects('active')
      setProjects(data.projects)
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    const onEvents = (e) => {
      const evs = e.detail || []
      if (evs.some(ev => ev.entity === 'project')) {
        fetchData()
      }
    }
    window.addEventListener('fb:events', onEvents)
    return () => window.removeEventListener('fb:events', onEvents)
  }, [fetchData])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.title || !formData.target_amount) return
    try {
      const payload = {
        ...formData,
        target_amount: parseFloat(formData.target_amount),
        saved_amount: parseFloat(formData.saved_amount) || 0,
      }
      if (editingId) {
        await api.updateProject({ ...payload, id: editingId })
      } else {
        await api.addProject(payload)
      }
      setShowForm(false)
      setEditingId(null)
      setFormData({ title: '', description: '', target_amount: '', saved_amount: '', target_month: '', priority: 'medium' })
      fetchData()
    } catch (err) {
      alert(err.message)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce projet ?')) return
    try {
      await api.deleteProject(id)
      fetchData()
    } catch (err) {
      alert(err.message)
    }
  }

  const handleComplete = async (project) => {
    try {
      await api.updateProject({ ...project, id: project.id, status: 'completed', saved_amount: project.target_amount })
      fetchData()
    } catch (err) {
      alert(err.message)
    }
  }

  const handleEdit = (project) => {
    setEditingId(project.id)
    setFormData({
      title: project.title,
      description: project.description || '',
      target_amount: String(project.target_amount),
      saved_amount: String(project.saved_amount),
      target_month: project.target_month || '',
      priority: project.priority,
    })
    setShowForm(true)
  }

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-3 border-primary-700 border-t-transparent rounded-full animate-spin" /></div>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Projets & Épargnes</h2>
        <button onClick={() => { setEditingId(null); setFormData({ title: '', description: '', target_amount: '', saved_amount: '', target_month: '', priority: 'medium' }); setShowForm(true) }} className="btn-primary flex items-center gap-1.5 px-3 py-2">
          <Plus size={18} /> Nouveau
        </button>
      </div>

      <div className="space-y-3">
        {projects.length === 0 && (
          <div className="card text-center py-8 text-slate-400">
            <Target size={32} className="mx-auto mb-2" />
            <p>Aucun projet actif</p>
            <p className="text-xs mt-1">Créez un projet pour épargner vers un objectif</p>
          </div>
        )}
        {projects.map((project) => {
          const progress = getPercentage(project.saved_amount, project.target_amount)
          const prio = priorityColors[project.priority] || priorityColors.medium
          return (
            <div key={project.id} className="card">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="font-semibold">{project.title}</h3>
                  {project.description && <p className="text-sm text-slate-500 mt-0.5">{project.description}</p>}
                </div>
                <span className={`badge ${prio.bg} ${prio.text} ${prio.border} border`}>{prio.label}</span>
              </div>

              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="text-slate-500">{formatCurrency(project.saved_amount)} épargné</span>
                <span className="font-medium">{formatCurrency(project.target_amount)}</span>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-primary-600 rounded-full transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">
                  {project.target_month ? `Échéance: ${project.target_month}` : 'Pas d\'échéance'}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(project)} className="text-xs text-primary-600 font-medium">Modifier</button>
                  <button onClick={() => handleComplete(project)} className="text-xs text-green-600 font-medium">Terminer</button>
                  <button onClick={() => handleDelete(project.id)} className="text-xs text-red-400">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl p-6 w-full max-w-sm max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{editingId ? 'Modifier' : 'Nouveau'} projet</h3>
              <button onClick={() => setShowForm(false)} className="p-1"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="label">Titre</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="input" placeholder="Ex: Voyage, nouvelle voiture..." required />
              </div>
              <div>
                <label className="label">Description (optionnel)</label>
                <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Montant cible</label>
                  <input type="number" value={formData.target_amount} onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })} className="input" placeholder="0" required />
                </div>
                <div>
                  <label className="label">Déjà épargné</label>
                  <input type="number" value={formData.saved_amount} onChange={(e) => setFormData({ ...formData, saved_amount: e.target.value })} className="input" placeholder="0" />
                </div>
              </div>
              <div>
                <label className="label">Mois cible (optionnel)</label>
                <input type="month" value={formData.target_month} onChange={(e) => setFormData({ ...formData, target_month: e.target.value })} className="input" />
              </div>
              <div>
                <label className="label">Priorité</label>
                <select value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })} className="input">
                  <option value="high">Haute</option>
                  <option value="medium">Moyenne</option>
                  <option value="low">Basse</option>
                </select>
              </div>
              <button type="submit" className="btn-primary w-full">{editingId ? 'Enregistrer' : 'Créer'}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
